import { Controller } from "@hotwired/stimulus"
import Sortable from "sortablejs"
import { Turbo } from "@hotwired/turbo-rails"

// Connects to data-controller="admin--section-element-sorter"
export default class extends Controller {
  static targets = ["list", "saveState"]
  static values = {
    pageId: Number,
    slotId: Number,
    canSort: Boolean,
    draftLockVersion: Number
  }

  connect() {
    this.autoHideSaveState()
    this.initializeSortable()
  }

  disconnect() {
    if (this.sortable) this.sortable.destroy()
  }

  initializeSortable() {
    if (!this.canSortValue || !this.hasListTarget) return

    this.sortable = new Sortable(this.listTarget, {
      animation: 150,
      handle: ".drag-handle",
      draggable: "[data-sortable-element-id]",
      onEnd: this.onEnd.bind(this)
    })
  }

  onEnd(event) {
    if (event.oldIndex === event.newIndex) return

    this.persistOrder(this.currentOrderedIds()).catch((error) => {
      console.error(error)
    })
  }

  currentOrderedIds() {
    return Array.from(this.listTarget.querySelectorAll("[data-sortable-element-id]"))
      .map((item) => Number(item.dataset.sortableElementId))
      .filter((id) => Number.isInteger(id) && id > 0)
  }

  async persistOrder(orderedIds) {
    if (!orderedIds.length) return

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    if (!csrfToken) return

    const response = await fetch(this.sortUrl(), {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "Accept": "text/vnd.turbo-stream.html",
        "X-CSRF-Token": csrfToken
      },
      body: JSON.stringify({
        page_section_slot_id: this.slotIdValue,
        ordered_ids: orderedIds,
        draft_lock_version: this.currentDraftLockVersion(),
        selected_element_id: this.currentSelectedElementId()
      })
    })

    const body = await response.text()
    const mediaType = response.headers.get("content-type") || ""
    if (mediaType.includes("text/vnd.turbo-stream.html")) {
      Turbo.renderStreamMessage(body)
    }

    if (!response.ok) {
      throw new Error(`Failed to persist order: ${response.status}`)
    }

    window.dispatchEvent(new CustomEvent("section-builder:mutation-success"))
  }

  sortUrl() {
    return `/admin/pages/${this.pageIdValue}/page_elements/sort`
  }

  currentSelectedElementId() {
    const workspace = document.getElementById("section_builder_workspace")
    if (!workspace) return null

    const rawValue = workspace.dataset.adminSectionWorkspaceSelectedElementIdValue
    if (!rawValue) return null

    const parsed = Number(rawValue)
    return Number.isInteger(parsed) && parsed > 0 ? parsed : null
  }

  currentDraftLockVersion() {
    const fromData = this.element.dataset.draftLockVersion
    if (fromData) {
      const parsed = Number(fromData)
      if (Number.isInteger(parsed) && parsed >= 0) return parsed
    }

    return this.draftLockVersionValue
  }

  autoHideSaveState() {
    if (!this.hasSaveStateTarget) return

    window.setTimeout(() => {
      if (!this.hasSaveStateTarget) return
      this.saveStateTarget.classList.add("opacity-0")
      window.setTimeout(() => {
        if (this.hasSaveStateTarget) this.saveStateTarget.remove()
      }, 250)
    }, 1200)
  }
}
